import { useState } from 'react';
import { CandidateLayout } from '@/components/layout/CandidateLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Bell,
  CheckCircle2,
  Calendar,
  FileText,
  XCircle,
  Briefcase,
  Check,
} from 'lucide-react';

interface Notification {
  id: string;
  title: string;
  message: string;
  company: string;
  time: string;
  type: 'shortlisted' | 'interview' | 'task' | 'rejected' | 'hired';
  read: boolean;
}

const CandidateNotifications = () => {
  const [notifications, setNotifications] = useState<Notification[]>([
    {
      id: '1',
      title: 'Congratulations! You have been shortlisted',
      message: 'TechCorp Inc. has shortlisted you for the Frontend Developer position.',
      company: 'TechCorp Inc.',
      time: '2 hours ago',
      type: 'shortlisted',
      read: false,
    },
    {
      id: '2',
      title: 'Interview Scheduled',
      message: 'Your interview with StartupXYZ has been scheduled for January 28, 2024 at 10:00 AM.',
      company: 'StartupXYZ',
      time: '1 day ago',
      type: 'interview',
      read: false,
    },
    {
      id: '3',
      title: 'New Task Assigned',
      message: 'InnovateTech has assigned you a coding task. Please complete it by January 30, 2024.',
      company: 'InnovateTech',
      time: '3 days ago',
      type: 'task',
      read: true,
    },
    {
      id: '4',
      title: 'Application Update',
      message: 'Unfortunately, GlobalTech has decided to move forward with other candidates.',
      company: 'GlobalTech',
      time: '5 days ago',
      type: 'rejected',
      read: true,
    },
    {
      id: '5',
      title: 'Congratulations! You are Hired',
      message: 'We are pleased to inform you that MegaCorp has extended an offer for the Senior Developer position.',
      company: 'MegaCorp',
      time: '1 week ago',
      type: 'hired',
      read: true,
    },
  ]);

  const getIcon = (type: Notification['type']) => {
    switch (type) {
      case 'shortlisted':
        return <CheckCircle2 className="h-5 w-5 text-primary" />;
      case 'interview':
        return <Calendar className="h-5 w-5 text-accent" />;
      case 'task':
        return <FileText className="h-5 w-5 text-muted-foreground" />;
      case 'rejected':
        return <XCircle className="h-5 w-5 text-destructive" />;
      case 'hired':
        return <Briefcase className="h-5 w-5 text-success" />;
      default:
        return <Bell className="h-5 w-5" />;
    }
  };

  const getBadgeVariant = (type: Notification['type']) => {
    switch (type) {
      case 'shortlisted':
        return 'default';
      case 'interview':
        return 'secondary';
      case 'task':
        return 'outline';
      case 'rejected':
        return 'destructive';
      case 'hired':
        return 'default';
      default:
        return 'outline';
    }
  };

  const markAsRead = (id: string) => {
    setNotifications(
      notifications.map((n) => (n.id === id ? { ...n, read: true } : n))
    );
  };

  const markAllAsRead = () => {
    setNotifications(notifications.map((n) => ({ ...n, read: true })));
  };

  const unreadCount = notifications.filter((n) => !n.read).length;

  return (
    <CandidateLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold">Notifications</h1>
            <p className="text-muted-foreground">
              Stay updated with your application status
            </p>
          </div>
          {unreadCount > 0 && (
            <Button variant="outline" onClick={markAllAsRead}>
              <Check className="mr-2 h-4 w-4" />
              Mark all as read ({unreadCount})
            </Button>
          )}
        </div>

        {/* Notifications List */}
        <div className="space-y-3">
          {notifications.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Bell className="h-12 w-12 text-muted-foreground/50 mb-4" />
                <p className="text-lg font-medium text-muted-foreground">
                  No notifications yet
                </p>
                <p className="text-sm text-muted-foreground">
                  You'll see updates about your applications here
                </p>
              </CardContent>
            </Card>
          ) : (
            notifications.map((notification) => (
              <Card
                key={notification.id}
                className={`transition-all ${
                  !notification.read
                    ? 'border-primary/50 bg-primary/5'
                    : 'hover:bg-muted/50'
                }`}
              >
                <CardContent className="flex items-start gap-4 p-4">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted">
                    {getIcon(notification.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-1">
                      <h3 className="font-semibold truncate">{notification.title}</h3>
                      <div className="flex items-center gap-2">
                        <Badge variant={getBadgeVariant(notification.type)}>
                          {notification.type}
                        </Badge>
                        {!notification.read && (
                          <span className="h-2 w-2 rounded-full bg-primary" />
                        )}
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground mb-2">
                      {notification.message}
                    </p>
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                      <span className="text-xs text-muted-foreground">
                        {notification.company} • {notification.time}
                      </span>
                      {!notification.read && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => markAsRead(notification.id)}
                        >
                          Mark as read
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    </CandidateLayout>
  );
};

export default CandidateNotifications;
